/**
 * Application Configuration
 *
 * Configuration for all target applications in the Bono ecosystem.
 * Maps each app to its URL and login path.
 */

import { TargetApp, type AppConfig } from '@/types';

/**
 * Application configurations
 * Maps each target app to its base URL and login path
 */
export const APP_CONFIGS: Record<TargetApp, AppConfig> = {
  [TargetApp.OPERATIONS]: {
    url: import.meta.env.VITE_OPERATIONS_APP_URL || '',
    loginPath: '/login',
    name: 'Operations App',
    app: TargetApp.OPERATIONS,
  },
  [TargetApp.CARBON_ACCOUNTING]: {
    url: import.meta.env.VITE_FRONTEND_URL || '',
    loginPath: '/login',
    name: 'Carbon Accounting',
    app: TargetApp.CARBON_ACCOUNTING,
  },
  [TargetApp.SUPPLY_CHAIN]: {
    url: import.meta.env.VITE_SUPPLY_CHAIN_URL || '',
    loginPath: '/login',
    name: 'Supply Chain',
    app: TargetApp.SUPPLY_CHAIN,
  },
  [TargetApp.ESG]: {
    url: import.meta.env.VITE_ESG_APP_URL || '',
    loginPath: '/login',
    name: 'ESG Platform',
    app: TargetApp.ESG,
  },
  [TargetApp.VENDOR]: {
    url: import.meta.env.VITE_VENDOR_PORTAL_URL || '',
    loginPath: '/login',
    name: 'Vendor Portal',
    app: TargetApp.VENDOR,
  },
  [TargetApp.SURVEY]: {
    url: import.meta.env.VITE_SURVEY_APP_URL || '',
    loginPath: '/login',
    name: 'Survey App',
    app: TargetApp.SURVEY,
  },
};

/**
 * Default app to redirect to when no specific role-based match
 */
export const DEFAULT_APP = TargetApp.CARBON_ACCOUNTING;

/**
 * Get app configuration by TargetApp
 */
export function getAppConfig(app: TargetApp): AppConfig {
  const config = APP_CONFIGS[app];

  if (!config) {
    console.warn(`App config not found for ${app}, using default`);
    return APP_CONFIGS[DEFAULT_APP];
  }

  return config;
}

/**
 * Get app URL
 */
export function getAppUrl(app: TargetApp): string {
  return getAppConfig(app).url;
}

/**
 * Get full login URL for an app
 */
export function getAppLoginUrl(app: TargetApp): string {
  const config = getAppConfig(app);
  // Remove trailing slash from URL and leading slash from path to avoid double slashes
  const baseUrl = config.url.replace(/\/$/, '');
  const path = config.loginPath.startsWith('/') ? config.loginPath : `/${config.loginPath}`;
  return `${baseUrl}${path}`;
}

/**
 * Validate that all required app URLs are configured
 * @throws Error if any required URL is missing
 */
export function validateAppConfigs(): void {
  const errors: string[] = [];

  Object.entries(APP_CONFIGS).forEach(([app, config]) => {
    if (!config.url) {
      errors.push(`Missing environment variable for ${app}`);
    }
  });

  if (errors.length > 0) {
    console.error('App configuration errors:', errors);
    throw new Error(`App configuration validation failed:\n${errors.join('\n')}`);
  }
}

/**
 * Get missing app configurations (for warnings, not errors)
 */
export function getMissingAppConfigs(): TargetApp[] {
  return Object.entries(APP_CONFIGS)
    .filter(([_, config]) => !config.url)
    .map(([app, _]) => app as TargetApp);
}

/**
 * Check if app is configured
 */
export function isAppConfigured(app: TargetApp): boolean {
  return Boolean(APP_CONFIGS[app]?.url);
}